import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getUserGroups } from 'actions';
import { getUsers, setUsers } from 'actions/userAction';
import { getCompanies } from 'actions/companyAction';
import { Table, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { getCurrentPlatform } from '../../utils/platform';
import { API } from '../../services/API';
import { getApiUrl } from '../../utils/platform';

class UserList extends Component {
  state = {
    showModal: false,
    isEdit: false,
    formData: {
      username: '',
      full_name: '',
      company_id: '',
      group_ids: [],
      password: '',
      is_active: true
    },
    error: null,
    companyFilter: '' // For Super Root company filtering
  };

  componentDidMount() {
    this.props.getUserGroups();
    this.props.getUsers();
    this.props.getCompanies();
  }

  // Helper method to determine user permissions
  getCurrentUserRole = () => {
    const currentUser = this.props.user;
    if (!currentUser) return 'none';
    
    const userGroups = currentUser.user_groups || currentUser.group_ids || currentUser.groups || [];
    const userGroupNames = userGroups.map(g => typeof g === 'string' ? g : g.group_name || g.name).filter(Boolean);
    
    if (userGroupNames.some(name => name.toLowerCase().includes('super root')) || currentUser.is_root === true) {
      return 'super_root';
    }
    if (userGroupNames.some(name => name.toLowerCase().includes('root'))) {
      return 'root';
    }
    if (userGroupNames.some(name => name.toLowerCase().includes('admin'))) {
      return 'admin';
    }
    return 'user';
  };

  // Helper method to handle company selection change
  handleCompanySelectChange = (selectedOption) => {
    this.setState(prev => ({
      formData: {
        ...prev.formData,
        company_id: selectedOption ? selectedOption.value : ''
      }
    }));
  };

  // Helper method to handle company filter change
  handleCompanyFilterChange = (selectedOption) => {
    this.setState({
      companyFilter: selectedOption ? selectedOption.value : ''
    });
  };

  handleShowModal = (user = null) => {
    if (user) {
      this.setState({
        showModal: true,
        isEdit: true,
        formData: {
          _id: user._id,
          username: user.username || '',
          full_name: user.full_name || '',
          company_id: user.company_id || '',
          group_ids: user.group_ids || [],
          password: '', // Không show password cũ
          is_active: user.is_active
        },
        error: null
      });
    } else {
      // For create mode, set company_id based on current user role
      const currentUserRole = this.getCurrentUserRole();
      const currentUser = this.props.user;
      let defaultCompanyId = '';
      
      if (currentUserRole === 'root' || currentUserRole === 'admin') {
        // Root/Admin: Use their own company_id
        defaultCompanyId = currentUser?.company_id || '';
      }
      // Super Root: Leave empty for selection
      // Other users: Leave empty (no permission)
      
      this.setState({
        showModal: true,
        isEdit: false,
        formData: {
          username: '',
          full_name: '',
          company_id: defaultCompanyId,
          group_ids: [],
          password: process.env.REACT_APP_DEFAULT_PASSWORD,
          is_active: true
        },
        error: null
      });
    }
  };

  handleCloseModal = () => {
    this.setState({ showModal: false, error: null });
  };

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState(prev => ({
      formData: {
        ...prev.formData,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  handleGroupCheckbox = (groupId) => {
    this.setState(prev => {
      const group_ids = prev.formData.group_ids.includes(groupId)
        ? prev.formData.group_ids.filter(id => id !== groupId)
        : [...prev.formData.group_ids, groupId];
      return {
        formData: {
          ...prev.formData,
          group_ids
        }
      };
    });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { isEdit, formData } = this.state;
    
    // Validation for company_id when creating new user
    if (!isEdit) {
      const currentUserRole = this.getCurrentUserRole();
      const currentUser = this.props.user;
      
      if (currentUserRole === 'root' || currentUserRole === 'admin') {
        // Ensure Root/Admin users create users in their own company
        if (!formData.company_id || formData.company_id !== currentUser?.company_id) {
          this.setState({ error: 'You can only create users in your own company.' });
          return;
        }
      } else if (currentUserRole === 'super_root') {
        // Super Root must select a company
        if (!formData.company_id) {
          this.setState({ error: 'Please select a company for the new user.' });
          return;
        }
      } else {
        // Other users cannot create users
        this.setState({ error: 'You do not have permission to create users.' });
        return;
      }
    }
    
    try {
      await this.props.setUsers([formData]);
      await this.props.getUsers();
      alert(isEdit ? 'User updated!' : 'User created!');
      this.handleCloseModal();
    } catch (err) {
      console.error('Submit error:', err);
      this.setState({ error: 'Save failed: ' + (err.message || 'Unknown error') });
    }
  };

  handleResetPassword = async (user) => {
    if (window.confirm(`Are you sure you want to reset password for user ${user.username}?`)) {
      try {
        const resetData = {
          ...user,
          password: process.env.REACT_APP_DEFAULT_PASSWORD,
          status: 'NEED_CHANGE_PASSWORD'
        };
        await this.props.setUsers([resetData]);
        await this.props.getUsers();
        alert(`Password has been reset to default: ${process.env.REACT_APP_DEFAULT_PASSWORD}`);
      } catch (err) {
        alert('Reset password failed!');
      }
    }
  };

  handleDeleteUser = async (user) => {
    if (window.confirm(`Are you sure you want to delete user ${user.username}? This action cannot be undone.`)) {
      try {
        const response = await API.post(`${getApiUrl()}/api/auth/delete-user`, {
          user_id: user._id
        });
        
        if (response.data && response.data.status === 1) {
          await this.props.getUsers();
          alert(`User ${user.username} has been deleted successfully!`);
        } else {
          alert('Delete user failed: ' + (response.data?.message || 'Unknown error'));
        }
      } catch (err) {
        console.error('Delete user error:', err);
        alert('Delete user failed: ' + (err.response?.data?.message || err.message || 'Unknown error'));
      }
    }
  };

  render() {
    const { userGroups = [], users = [], companies = [], loading } = this.props;
    const { showModal, formData, error, isEdit, companyFilter } = this.state;
    
    // Filter users based on platform and current user
    const isTKT1 = getCurrentPlatform() === 'TKT1-landing' || getCurrentPlatform() === 'TKT1-app';
    const currentUser = this.props.user;
    const currentUserGroups = currentUser?.user_groups || currentUser?.group_ids || currentUser?.groups || [];
    const isCurrentUserRoot = Array.isArray(currentUserGroups) ? currentUserGroups.includes('Root') : false;
    
    // Ensure userGroups and users are always arrays
    const safeUserGroups = Array.isArray(userGroups) ? userGroups : [];
    const safeUsers = Array.isArray(users) ? users : [];
    
    // Helper: map id sang tên
    const groupIdToName = {};
    safeUserGroups.forEach(g => {
      groupIdToName[g._id] = g.name || g.group_name;
    });
    
    // Helper: map company_id sang company name
    const safeCompanies = Array.isArray(companies) ? companies : [];
    const companyIdToName = {};
    safeCompanies.forEach(c => {
      companyIdToName[c.company_id] = c.name;
    });
    
    // Get current user role for UI logic
    const currentUserRole = this.getCurrentUserRole();
    
    // Debug: Log current user info
    console.log('Current user:', currentUser);
    console.log('Is TKT1:', isTKT1);
    console.log('Is current user Root:', isCurrentUserRoot);
    console.log('Current user role:', currentUserRole);
    console.log('All users:', safeUsers);
    
    // In TKT1, only Root users can see Root users, but Super Root can see all users
    const filteredUsers = isTKT1 && !isCurrentUserRoot && currentUserRole !== 'super_root' ? safeUsers.filter(user => {
      // Check multiple possible field names for user groups
      const userGroups = user.user_groups || user.group_ids || user.groups || [];
      const hasRootGroup = Array.isArray(userGroups) ? userGroups.includes('Root') : false;
      
      // Also check if user has Root in their group names (for display purposes)
      const userGroupNames = (user.group_ids || []).map(id => groupIdToName[id]).filter(Boolean);
      const hasRootInGroupNames = userGroupNames.some(name => name === 'Root');
      
      console.log(`User ${user.username} groups:`, userGroups, 'group names:', userGroupNames, 'has Root:', hasRootGroup || hasRootInGroupNames);
      return !(hasRootGroup || hasRootInGroupNames);
    }) : safeUsers;
    
    console.log('Filtered users:', filteredUsers);
    
    // Apply company filter for Super Root users
    let finalFilteredUsers = filteredUsers;
    if (currentUserRole === 'super_root' && companyFilter) {
      finalFilteredUsers = filteredUsers.filter(user => user.company_id === companyFilter);
    }
    
    console.log('Final filtered users:', finalFilteredUsers);
    
    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      );
    }
    
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            {(() => {
              if (currentUserRole === 'super_root') {
                return <h3>User List - All Companies</h3>;
              } else if (currentUserRole === 'root' || currentUserRole === 'admin') {
                const currentCompany = safeCompanies.find(c => c.company_id === currentUser?.company_id);
                const companyName = currentCompany ? currentCompany.name : (currentUser?.company_id || 'Unknown Company');
                return <h3>Quản lý user công ty {companyName}</h3>;
              } else {
                return <h3>User List</h3>;
              }
            })()}
          </div>
          <Button variant="primary" onClick={() => this.handleShowModal()}>Create New User</Button>
        </div>
        
        {/* Company Filter for Super Root */}
        {currentUserRole === 'super_root' && (
          <div className="mb-3">
            <div className="row">
              <div className="col-md-4">
                <label className="form-label">Filter by Company:</label>
                <Select
                  options={[
                    { value: '', label: 'All Companies' },
                    ...safeCompanies.map(company => ({
                      value: company.company_id,
                      label: `${company.name} (${company.company_id})`
                    }))
                  ]}
                  value={safeCompanies.length > 0 && companyFilter ? 
                    { 
                      value: companyFilter, 
                      label: `${safeCompanies.find(c => c.company_id === companyFilter)?.name || companyFilter} (${companyFilter})`
                    } : 
                    { value: '', label: 'All Companies' }
                  }
                  onChange={this.handleCompanyFilterChange}
                  placeholder="Select company to filter..."
                  isSearchable={true}
                  isClearable={true}
                />
              </div>
            </div>
          </div>
        )}
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Username</th>
              <th>Full Name</th>
              {currentUserRole === 'super_root' && <th>Company</th>}
              <th>Groups</th>
              <th>Active</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={currentUserRole === 'super_root' ? 6 : 5} className="text-center"><Spinner animation="border" size="sm" /></td></tr>
            ) : finalFilteredUsers.length === 0 ? (
              <tr><td colSpan={currentUserRole === 'super_root' ? 6 : 5} className="text-center">No users</td></tr>
            ) : finalFilteredUsers.map((u, idx) => (
              <tr key={u._id || idx}>
                <td>{u.username}</td>
                <td>{u.full_name}</td>
                {currentUserRole === 'super_root' && <td>{companyIdToName[u.company_id] || u.company_id || '-'}</td>}
                <td>{(u.group_ids || u.user_groups || u.groups || []).map(id => groupIdToName[id]).filter(Boolean).join(', ')}</td>
                <td>{u.is_active ? 'Yes' : 'No'}</td>
                <td>
                  <Button size="sm" variant="info" className="me-2" onClick={() => this.handleShowModal(u)}>Edit</Button>
                  <Button size="sm" variant="warning" className="me-2" onClick={() => this.handleResetPassword(u)}>Reset Password</Button>
                  <Button size="sm" variant="danger" onClick={() => this.handleDeleteUser(u)}>Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Modal show={showModal} onHide={this.handleCloseModal} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>{isEdit ? 'Edit User' : 'Create New User'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form onSubmit={this.handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  name="username"
                  value={formData.username}
                  onChange={this.handleChange}
                  required
                  disabled={isEdit}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Full Name</Form.Label>
                <Form.Control
                  name="full_name"
                  value={formData.full_name}
                  onChange={this.handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Company</Form.Label>
                {(() => {
                  const currentUserRole = this.getCurrentUserRole();
                  const currentUser = this.props.user;
                  const safeCompanies = Array.isArray(companies) ? companies : [];
                  
                  if (currentUserRole === 'super_root') {
                    // Super Root: Show searchable dropdown with all companies
                    const companyOptions = safeCompanies.map(company => ({
                      value: company.company_id,
                      label: `${company.name} (${company.company_id})`
                    }));
                    
                    const selectedCompany = companyOptions.find(option => option.value === formData.company_id);
                    
                    return (
                      <Select
                        options={companyOptions}
                        value={selectedCompany || null}
                        onChange={this.handleCompanySelectChange}
                        placeholder="Select a company..."
                        isSearchable={true}
                        isClearable={true}
                      />
                    );
                  } else if (currentUserRole === 'root' || currentUserRole === 'admin') {
                    // Root/Admin: Show current user's company only (readonly)
                    const currentCompany = safeCompanies.find(c => c.company_id === currentUser?.company_id);
                    const displayValue = currentCompany ? `${currentCompany.name} (${currentCompany.company_id})` : (currentUser?.company_id || '');
                    
                    return (
                      <>
                        <Form.Control
                          value={displayValue}
                          disabled
                          readOnly
                          placeholder="Current user's company"
                        />
                        <Form.Text className="text-muted">
                          You can only create users in your own company.
                        </Form.Text>
                      </>
                    );
                  } else {
                    // Other users: No company selection allowed
                    return (
                      <Form.Control
                        value="No permission to assign company"
                        disabled
                        readOnly
                      />
                    );
                  }
                })()}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Groups</Form.Label>
                <div>
                  {safeUserGroups.map(g => (
                    <Form.Check
                      key={g._id}
                      type="checkbox"
                      label={g.name || g.group_name}
                      checked={formData.group_ids.includes(g._id)}
                      onChange={() => this.handleGroupCheckbox(g._id)}
                    />
                  ))}
                </div>
              </Form.Group>
              {!isEdit && (
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="text"
                    value={`Default password: ${process.env.REACT_APP_DEFAULT_PASSWORD}`}
                    disabled
                    readOnly
                  />
                </Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={this.handleChange}
                />
              </Form.Group>
              <Button type="submit" variant="primary">{isEdit ? 'Save' : 'Create'}</Button>
            </Form>
          </Modal.Body>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userGroups: state.userGroupsReducer.userGroups || [],
  users: state.userReducer.users || [],
  companies: state.companyReducer.companies || [],
  loading: state.userReducer.loading,
  user: state.authenReducer.user
});

export default connect(mapStateToProps, { getUserGroups, getUsers, setUsers, getCompanies })(UserList); 